import { useQuery } from 'modules/core/hooks/useQuery'
import { F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import {
  IncentivesCompensationPlanPayoutRequestBreakdownFragment,
  IncentivesCompensationPlanPayoutEstimationBreakdownFragment,
  IncentivesCompensationPlanRequestBreakdownDocument,
  IncentivesPayoutBreakdownAchievementFragment,
} from 'generated/resources/graphql'

import type { BreakdownData, PayoutBreakdownData } from './types'
import { PayoutBreakdownSkeleton } from './PayoutBreakdownSkeleton'

interface PayoutBreakdownProps {
  payout: PayoutBreakdownData
  onClose: () => void
}

function mapBreakdown(
  payout:
    | IncentivesCompensationPlanPayoutRequestBreakdownFragment
    | IncentivesCompensationPlanPayoutEstimationBreakdownFragment
): BreakdownData | null {
  if ('baseAmountCents' in payout) {
    return {
      baseAmountCents: payout.baseAmountCents,
      amountCents: payout.amountCents,
      payoutPercentage: payout.payoutPercentage,
      achievements: payout.achievements,
      currency: payout.currency,
    }
  }
  const payoutEstimation = payout.payoutEstimation
  return {
    baseAmountCents: payoutEstimation?.baseAmountCents ?? null,
    amountCents: payoutEstimation?.amountCents ?? null,
    payoutPercentage: payoutEstimation?.payoutPercentage ?? null,
    achievements: payoutEstimation?.achievements ?? [],
    currency: payout.employee?.legalEntity?.currency ?? null,
  }
}

function formatNumber(cents: number | null | undefined, currency: string | null): string {
  if (cents == null) return '-'
  if (!currency) return i18n.formatNumber(cents / 100, 'decimal')
  return i18n.formatNumber(cents / 100, 'currency', currency)
}

export const PayoutBreakdown = ({ payout, onClose }: PayoutBreakdownProps) => {
  const requestId = payout && 'requestId' in payout ? payout.requestId : undefined
  const { data: requestData, loading } = useQuery(
    IncentivesCompensationPlanRequestBreakdownDocument,
    {
      variables: { id: requestId ?? '' },
      skip: !requestId,
    }
  )

  const request = requestData?.incentives?.compensationPlanRequest
  const breakdown = payout.estimation
    ? mapBreakdown(payout.estimation)
    : request
      ? mapBreakdown(request)
      : null

  return (
    <F0Dialog isOpen onClose={onClose} title={i18n.t('incentives.payout_breakdown.title')}>
      <F0Box display="flex" flexDirection="column" gap="lg">
        {loading && <PayoutBreakdownSkeleton />}
        {!loading && !breakdown && (
          <F0Box display="flex" justifyContent="center" paddingY="lg">
            <F0Text variant="description" content={i18n.t('incentives.payout_breakdown.no_data')} />
          </F0Box>
        )}
        {!loading && breakdown && (
          <>
            {breakdown.achievements && (
              <PayoutBreakdownTable achievements={breakdown.achievements} />
            )}

            <F0Box
              borderTop="default"
              borderBottom="default"
              paddingY="md"
              display="flex"
              flexDirection="column"
              gap="sm"
            >
              {breakdown.payoutPercentage != null && (
                <F0Box display="flex" justifyContent="between" alignItems="center">
                  <F0Text
                    variant="description"
                    content={i18n.t('incentives.payout_breakdown.avg_achievement')}
                  />
                  <F0Text
                    variant="label"
                    content={`${i18n.formatNumber(breakdown.payoutPercentage, 'decimal')}%`}
                  />
                </F0Box>
              )}
            </F0Box>

            <F0Box display="flex" justifyContent="between" alignItems="center">
              <F0Text
                variant="body"
                content={
                  breakdown.payoutPercentage != null && breakdown.baseAmountCents != null
                    ? `${formatNumber(breakdown.baseAmountCents, breakdown.currency)} × ${i18n.formatNumber(breakdown.payoutPercentage, 'decimal')}%`
                    : '-'
                }
              />
              <span className="text-f1-foreground-positive text-base font-medium">
                {formatNumber(breakdown.amountCents, breakdown.currency)}
              </span>
            </F0Box>
          </>
        )}
      </F0Box>
    </F0Dialog>
  )
}

const PayoutBreakdownTable = ({
  achievements,
}: {
  achievements: IncentivesPayoutBreakdownAchievementFragment[]
}) => {
  if (achievements.length === 0) {
    return null
  }

  return (
    <F0Box display="flex" flexDirection="column">
      <F0Box display="flex" paddingY="sm">
        <F0Box grow>
          <F0Text variant="small" content={i18n.t('incentives.payout_breakdown.goal')} />
        </F0Box>
        <F0Box display="flex" justifyContent="end" width="24">
          <F0Text
            variant="small"
            content={i18n.t('incentives.payout_breakdown.achievement')}
            align="right"
          />
        </F0Box>
        <F0Box display="flex" justifyContent="end" width="16">
          <F0Text
            variant="small"
            content={i18n.t('incentives.payout_breakdown.weight')}
            align="right"
          />
        </F0Box>
        <F0Box display="flex" justifyContent="end" width="16">
          <F0Text
            variant="small"
            content={i18n.t('incentives.payout_breakdown.result')}
            align="right"
          />
        </F0Box>
      </F0Box>
      {achievements.map(
        ({
          achievementPercentage,
          weight,
          payoutPercentage,
          compensableMetricLabel,
          compensationPlanMetricId,
        }) => (
          <F0Box
            key={compensationPlanMetricId ?? compensableMetricLabel}
            display="flex"
            paddingY="sm"
          >
            <F0Box grow>
              <F0Text variant="label" content={compensableMetricLabel ?? '-'} />
            </F0Box>
            <F0Box display="flex" justifyContent="end" width="24">
              <span className="text-f1-foreground-positive text-base font-medium">
                {i18n.formatNumber(achievementPercentage, 'decimal')}%
              </span>
            </F0Box>
            <F0Box display="flex" justifyContent="end" width="16">
              <F0Text
                variant="label"
                content={`${i18n.formatNumber(weight, 'decimal')}%`}
                align="right"
              />
            </F0Box>
            <F0Box display="flex" justifyContent="end" width="16">
              <F0Text
                variant="label"
                content={`${i18n.formatNumber(payoutPercentage, 'decimal')}%`}
                align="right"
              />
            </F0Box>
          </F0Box>
        )
      )}
    </F0Box>
  )
}
